import { useEffect, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';

import angleUpIcon from 'assets/angle-up.svg';

import {
  generateDesignReviewShareLink,
  getDesignReviewShareLink,
  updateDesignReviewShareLinkScope,
} from 'services/teamDashboardReviews';

import copyValue from 'utlis/copy_value';
import { errorReactToastify, successReactToastify } from 'utlis/toasts';

const SHARE_ACCESS_OPTIONS = {
  DOMAIN: 'domain',
  PUBLIC: 'public',
};

const ShareReviewModal = ({ isOpen, onClose, reviewUuid }) => {
  const [shareableLink, setShareableLink] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [shareState, setShareState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(SHARE_ACCESS_OPTIONS.DOMAIN);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isUpdatingScope, setIsUpdatingScope] = useState(false);

  const dropdownRef = useRef(null);

  const isPersonalDomain = shareState?.is_personal_domain ?? true;
  const isShareLinkGenerated = shareState?.is_share_link_generated ?? false;

  const placeholderLink = `${location.origin}/share/E4duaPyBaqQnRGFtVZ1UPH_32_PAp47cyGFbdrPuymE`;
  const linkDisplayText = isShareLinkGenerated && shareableLink ? shareableLink : placeholderLink;

  useEffect(() => {
    if (!isOpen || !reviewUuid) {
      return;
    }

    // eslint-disable-next-line compat/compat -- IE11 not in browserslist
    const abortController = new AbortController();

    const fetchShareState = async () => {
      setLoading(true);
      setFetchError(null);

      try {
        const data = await getDesignReviewShareLink(reviewUuid, abortController.signal);

        setShareState(data);
        setSelectedAccess(
          data?.scope === SHARE_ACCESS_OPTIONS.PUBLIC ? SHARE_ACCESS_OPTIONS.PUBLIC : SHARE_ACCESS_OPTIONS.DOMAIN
        );
        setDropdownOpen(false);

        if (data?.is_share_link_generated && data?.url) {
          setShareableLink(data.url);
        }
      }
      catch (err) {
        if (err?.code === 'ERR_CANCELED') {
          return;
        }

        setFetchError(err.message || 'Failed to load share settings');
      }
      finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchShareState();

    return () => {
      abortController.abort();
    };
  }, [isOpen, reviewUuid]);

  useEffect(() => {
    if (!dropdownOpen) {
      return;
    }

    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleCopyLink = async () => {
    if (!shareableLink) {
      return;
    }

    try {
      await copyValue(shareableLink);
      setIsCopied(true);
      successReactToastify('Link copied to clipboard!');
    }
    catch (error) {
      errorReactToastify('Failed to copy link to clipboard');
    }
  };

  const handleGenerateClick = async () => {
    if (!reviewUuid || isGenerating) {
      return;
    }
    setIsGenerating(true);

    try {
      const data = await generateDesignReviewShareLink(reviewUuid, selectedAccess);

      setShareableLink(data?.url ?? '');
      setShareState(prev => ({
        ...prev,
        ...data,
        is_share_link_generated: data?.is_share_link_generated ?? true,
      }));
      setSelectedAccess(
        data?.scope === SHARE_ACCESS_OPTIONS.PUBLIC ? SHARE_ACCESS_OPTIONS.PUBLIC : SHARE_ACCESS_OPTIONS.DOMAIN
      );
    }
    catch (err) {
      const message = err?.response?.data?.detail
        || err.message
        || 'Failed to generate share link';

      errorReactToastify(message);
    }
    finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setIsCopied(false);
    setIsGenerating(false);
    setIsUpdatingScope(false);
    setDropdownOpen(false);
    setShareState(null);
    setShareableLink('');
    setFetchError(null);
    onClose();
  };

  const toggleDropdown = () => {
    setDropdownOpen(prev => !prev);
  };

  const getAccessLabel = (option, userDomain) => {
    if (option === SHARE_ACCESS_OPTIONS.DOMAIN) {
      return userDomain ? `Anyone at ${userDomain}` : 'Anyone within domain';
    }

    return 'Anyone with the link';
  };

  const handleScopeChange = async newAccess => {
    const newScope = newAccess;

    setSelectedAccess(newAccess);
    setDropdownOpen(false);

    const shouldUpdateScope = isShareLinkGenerated
      && shareState?.scope
      && shareState.scope !== newScope;

    if (!shouldUpdateScope || !reviewUuid) {
      return;
    }

    setIsUpdatingScope(true);

    try {
      const data = await updateDesignReviewShareLinkScope(reviewUuid, newScope);

      setShareState(prev => ({
        ...prev,
        ...data,
      }));

      if (data?.url) {
        setShareableLink(data.url);
      }

      successReactToastify('Access updated');
    }
    catch (err) {
      setSelectedAccess(
        shareState?.scope === SHARE_ACCESS_OPTIONS.PUBLIC ? SHARE_ACCESS_OPTIONS.PUBLIC : SHARE_ACCESS_OPTIONS.DOMAIN
      );

      const message = err?.response?.data?.detail
        || err.message
        || 'Failed to update access';

      errorReactToastify(message);
    }
    finally {
      setIsUpdatingScope(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 9999, backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="absolute inset-0 bg-gray-900 bg-opacity-30"
        onClick={handleClose}
      />

      <div
        className="relative flex flex-col bg-white border border-gray-200 rounded-2xl shadow-lg"
        style={{
          width: 'min(760px, 95vw)',
          height: 'min(212px, 80vh)',
        }}
      >
        <div className="flex justify-between items-center w-full px-24 py-16 shrink-0">
          <h2 className="font-semibold text-base leading-5 text-gray-900">
            Share this audit
          </h2>
          <button
            onClick={handleClose}
            className="w-5 h-5 flex items-center justify-center shrink-0"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5l10 10" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="flex flex-col w-full flex-1 min-h-0 border-t border-gray-200">
          {loading
            ? (
              <div className="flex items-center justify-center flex-1 w-full">
                <ImpulseSpinner frontColor="#28BDAE" />
              </div>
            )
            : fetchError
              ? (
                <div className="flex items-center px-24 py-16 w-full flex-1">
                  <p className="font-normal text-sm leading-5 text-red-600">
                    {fetchError}
                  </p>
                </div>
              )
              : (
                <>
                  <div className="flex flex-col w-full border-b border-gray-200">
                    <div
                      className="flex items-center mx-24 my-24 h-14 bg-white border
                        border-gray-300 rounded-lg px-16 py-12"
                    >
                      <div
                        className="flex-1 min-w-0 truncate mr-8 text-sm leading-5"
                        style={{
                          color: isShareLinkGenerated ? '#111827' : '#CBCFD6',
                        }}
                      >
                        {linkDisplayText}
                      </div>
                      {isShareLinkGenerated && shareableLink
                        ? (
                          <button
                            onClick={handleCopyLink}
                            className="flex items-center justify-center px-12 py-8 bg-primary-500 rounded-lg"
                            style={{ width: '100px' }}
                          >
                            <span className="h-4 text-xs leading-4 text-white">
                              {isCopied ? 'Link copied!' : 'Copy link'}
                            </span>
                          </button>
                        )
                        : (
                          <button
                            type="button"
                            onClick={handleGenerateClick}
                            disabled={isGenerating}
                            className="flex items-center justify-center px-12 py-8
                              bg-primary-500 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
                            style={{ width: '100px' }}
                          >
                            {isGenerating
                              ? (
                                <span className="h-4 text-xs leading-4 text-white">
                                  Generating...
                                </span>
                              )
                              : (
                                <span className="h-4 text-xs leading-4 text-white">
                                  Generate link
                                </span>
                              )}
                          </button>
                        )}
                    </div>
                  </div>

                  <div className="flex items-center px-24 py-16 w-full">
                    {isPersonalDomain
                      ? (
                        <span className="font-normal text-sm leading-5 text-gray-600">
                          Anyone with the link can view
                        </span>
                      )
                      : (
                        <div className="relative" ref={dropdownRef}>
                          <button
                            type="button"
                            onClick={toggleDropdown}
                            disabled={isUpdatingScope}
                            className="flex items-center font-normal text-sm leading-5 text-gray-600
                              cursor-pointer disabled:opacity-70"
                            style={{ gap: '8px' }}
                          >
                            <span>
                              {getAccessLabel(selectedAccess, shareState?.user_domain)}
                            </span>
                            <img
                              src={angleUpIcon}
                              alt=""
                              className="w-[10px] h-[6px]"
                              style={{
                                transform: dropdownOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                              }}
                            />
                          </button>
                          {dropdownOpen && (
                            <div
                              className="absolute left-0 top-full bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                              style={{
                                padding: 8,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                width: 'max-content',
                                marginTop: 6,
                              }}
                              role="listbox"
                            >
                              <button
                                type="button"
                                role="option"
                                aria-selected={selectedAccess === SHARE_ACCESS_OPTIONS.DOMAIN}
                                onClick={() => handleScopeChange(SHARE_ACCESS_OPTIONS.DOMAIN)}
                                className="block w-full text-left py-2 px-3 text-sm leading-5
                                  text-gray-900 hover:bg-gray-100 font-normal whitespace-nowrap"
                              >
                                {getAccessLabel(SHARE_ACCESS_OPTIONS.DOMAIN, shareState?.user_domain)}
                              </button>
                              <button
                                type="button"
                                role="option"
                                aria-selected={selectedAccess === SHARE_ACCESS_OPTIONS.PUBLIC}
                                onClick={() => handleScopeChange(SHARE_ACCESS_OPTIONS.PUBLIC)}
                                className="block w-full text-left py-2 px-3 text-sm leading-5
                                  text-gray-900 hover:bg-gray-100 font-normal whitespace-nowrap"
                              >
                                Anyone with the link
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </>
              )}
        </div>
      </div>
    </div>
  );
};

ShareReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reviewUuid: PropTypes.string.isRequired,
};

export default ShareReviewModal;
